import { getApp, getApps, initializeApp, type FirebaseOptions } from 'firebase/app';
import { getAnalytics, isSupported, logEvent, type Analytics } from 'firebase/analytics';

declare global {
  interface Window {
    __ARKRON_FIREBASE_CONFIG__?: Partial<FirebaseOptions>;
  }
}

type AnalyticsEventParams = Record<string, string | number | boolean | undefined>;

let analyticsPromise: Promise<Analytics | null> | null = null;
let lastTrackedPage = '';

function getFirebaseConfig(): FirebaseOptions | null {
  const config = window.__ARKRON_FIREBASE_CONFIG__;
  if (!config) return null;
  if (!config.apiKey || !config.appId || !config.projectId || !config.measurementId) {
    return null;
  }

  return {
    apiKey: config.apiKey,
    authDomain: config.authDomain,
    projectId: config.projectId,
    storageBucket: config.storageBucket,
    messagingSenderId: config.messagingSenderId,
    appId: config.appId,
    measurementId: config.measurementId,
  };
}

async function getAnalyticsInstance(): Promise<Analytics | null> {
  if (!analyticsPromise) {
    analyticsPromise = isSupported()
      .then((supported) => {
        if (!supported) return null;

        const config = getFirebaseConfig();
        if (!config) return null;

        const app = getApps().length > 0 ? getApp() : initializeApp(config);
        return getAnalytics(app);
      })
      .catch(() => null);
  }

  return analyticsPromise;
}

function sendEvent(name: string, params: AnalyticsEventParams): void {
  void getAnalyticsInstance().then((analytics) => {
    if (!analytics) return;
    logEvent(analytics, name, params);
  });
}

function getActivePageTitle(): string {
  const activeHeading = document.querySelector<HTMLElement>('.page.active h1');
  const headingText = activeHeading?.textContent?.trim();
  if (headingText) return headingText;
  return document.title || 'ARKRON';
}

export function initAnalytics(): void {
  void getAnalyticsInstance();
}

export function trackPageView(path: string, hash = ''): void {
  const pageKey = hash ? `${path}#${hash}` : path;
  if (pageKey === lastTrackedPage) return;
  lastTrackedPage = pageKey;

  sendEvent('page_view', {
    page_title: getActivePageTitle(),
    page_location: `${window.location.origin}${pageKey}`,
    page_path: path,
  });
}

export function trackSectorChange(sector: 'tech' | 'labs'): void {
  sendEvent('select_content', {
    content_type: 'sector',
    item_id: sector,
  });
}

export function trackContactSubmission(topic: string): void {
  sendEvent('generate_lead', {
    form_name: 'contact',
    topic: topic || 'general',
  });
}

type ClickTrackingPayload = {
  href: string;
  label: string;
  sourcePath: string;
  sourceSection?: string;
};

function normalizePathname(pathname: string): string {
  const normalized = pathname.replace(/\/$/, '');
  return normalized || '/';
}

function getSourceSection(target: HTMLElement): string | undefined {
  const section = target.closest<HTMLElement>('[data-route-anchor], section[id], .section, .page');
  if (!section) return undefined;

  const routeAnchor = section.dataset.routeAnchor?.trim();
  if (routeAnchor) return routeAnchor;
  if (section.id) return section.id;
  if (section.classList.contains('page')) return section.className.replace(/\s+/g, '-');
  return undefined;
}

function isInternalContactRoute(href: string): boolean {
  try {
    const url = new URL(href, window.location.origin);
    return url.origin === window.location.origin && normalizePathname(url.pathname) === '/contact-us';
  } catch {
    return false;
  }
}

function isCareerInterestClick(target: HTMLElement, href: string): boolean {
  if (!isInternalContactRoute(href)) return false;
  return !!target.closest('#career-open-positions');
}

function getClickPayload(target: HTMLElement, href: string): ClickTrackingPayload {
  return {
    href,
    label: target.textContent?.trim() || 'unknown',
    sourcePath: normalizePathname(window.location.pathname),
    sourceSection: getSourceSection(target),
  };
}

export function trackContactCtaClick(payload: ClickTrackingPayload): void {
  sendEvent('contact_cta_click', {
    link_url: payload.href,
    link_text: payload.label,
    source_path: payload.sourcePath,
    source_section: payload.sourceSection,
  });
}

export function trackServiceCardOpen(serviceName: string, href: string, sourcePath: string): void {
  sendEvent('service_card_open', {
    service_name: serviceName || 'unknown',
    link_url: href,
    source_path: sourcePath,
  });
}

export function trackCareerInterest(payload: ClickTrackingPayload): void {
  sendEvent('career_interest', {
    link_url: payload.href,
    link_text: payload.label,
    source_path: payload.sourcePath,
    source_section: payload.sourceSection,
  });
}

export function bindAnalyticsInteractions(): void {
  document.addEventListener('click', (event) => {
    const target = event.target instanceof HTMLElement ? event.target : null;
    if (!target) return;

    const link = target.closest<HTMLAnchorElement>('a[href]');
    if (!link) return;

    const href = link.getAttribute('href')?.trim();
    if (!href) return;

    if (link.closest('.service-card')) {
      const title = link.closest('.service-card')?.querySelector('h3')?.textContent?.trim() || 'unknown';
      trackServiceCardOpen(title, href, normalizePathname(window.location.pathname));
      return;
    }

    const payload = getClickPayload(link, href);

    if (isCareerInterestClick(link, href)) {
      trackCareerInterest(payload);
      return;
    }

    if (isInternalContactRoute(href)) {
      trackContactCtaClick(payload);
    }
  });
}
